import { onBeforeUnmount } from "vue";

export interface LatestRequestHandle {
	requestId: number;
	controller: AbortController;
	signal: AbortSignal;
}

export interface LatestRequestScope {
	startRequest: () => LatestRequestHandle;
	isCurrent: (requestId: number) => boolean;
	isSuperseded: (requestId: number) => boolean;
	abortCurrent: () => void;
}

export function createLatestRequestScope(): LatestRequestScope {
	let currentRequestId = 0;
	let currentController: AbortController | null = null;

	return {
		startRequest() {
			currentController?.abort();
			currentController = new AbortController();
			currentRequestId += 1;

			return {
				requestId: currentRequestId,
				controller: currentController,
				signal: currentController.signal
			};
		},
		isCurrent(requestId: number) {
			return currentController !== null && !currentController.signal.aborted && requestId === currentRequestId;
		},
		// A request is superseded only when a strictly newer one has started. Unlike
		// `isCurrent`, this is independent of abort state — the latest request being
		// aborted (e.g. on unmount/route change) does NOT make it "superseded". Owners
		// of loading state should clear it when a request is no longer superseded so an
		// aborted-but-not-replaced request cannot leave the UI stuck loading (LSP-15911).
		isSuperseded(requestId: number) {
			return requestId !== currentRequestId;
		},
		abortCurrent() {
			currentController?.abort();
			currentController = null;
		}
	};
}

export function useLatestRequest(): LatestRequestScope {
	const requestScope = createLatestRequestScope();

	onBeforeUnmount(() => {
		requestScope.abortCurrent();
	});

	return requestScope;
}
